import { useState, useRef, useCallback, useEffect } from 'react';
import {
    Button,
    TextField,
    Card,
    CardContent,
    Typography,
    IconButton,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    MenuItem,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { LoadingSection } from '../../common/LoadingSection';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { goApi } from '../../services';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircle from '@mui/icons-material/AddCircle';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

const PAGE_SIZE = 20;

const QuestionsView = ({ questions, setQuestions, errors, setErrors, page, setPage, scrollToQuestion, setScrollToQuestion, }: any) => {
    const [loading, setLoading] = useState(false);
    const paginatedQuestions = questions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    const totalPages = Math.ceil(questions.length / PAGE_SIZE);
    const questionRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

    useEffect(() => {
        if (scrollToQuestion !== null) {
            const el = questionRefs.current[scrollToQuestion];
            if (el) {
                el.scrollIntoView({ behavior: 'auto', block: 'center' });

                // reset after scrolling
                setTimeout(() => {
                    setScrollToQuestion(null);
                }, 50);
            }
        }
    }, [scrollToQuestion, page, setScrollToQuestion]);

    const handleQuestionChange = (index: number, field: string, value: any) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index][field] = value;
        setQuestions(updatedQuestions);
        setErrors((prev: any) => {
            if (!prev[index]) return prev;
            const copy = { ...prev };
            delete copy[index].question;
            return copy;
        });
    };
    const hasQuestionError = (index: number) => {
        return Boolean(errors?.[index]);
    };


    const handleOptionChange = (qIndex: number, oIndex: number, value: string) => {
        const updatedQuestions = [...questions];
        updatedQuestions[qIndex].options[oIndex].content = value;
        setQuestions(updatedQuestions);
        setErrors((prev: any) => {
            if (!prev[qIndex]?.options?.[oIndex]) return prev;
            const copy = { ...prev };
            delete copy[qIndex].options[oIndex];
            return copy;
        });
    };

    const handleSetCorrectOption = (qIndex: number, oIndex: number) => {
        const updatedQuestions = [...questions];
        updatedQuestions[qIndex].options.forEach((opt: any, idx: any) => {
            opt.isCorrect = idx === oIndex;
        });
        setQuestions(updatedQuestions);
        setErrors((prev: any) => {
            if (!prev[qIndex]?.correctOption) return prev;
            const copy = { ...prev };
            delete copy[qIndex].correctOption;
            return copy;
        });
    };
    const handlePageChange = (newPage: number) => {
        setLoading(true);
        setTimeout(() => {
            setPage(newPage);
            setLoading(false);
        }, 300);
    };
    const handleDeleteOption = (qIndex: number, oIndex: number) => {
        const updatedQuestions = [...questions];
        updatedQuestions[qIndex].options.splice(oIndex, 1);
        setQuestions(updatedQuestions);
    };
    const handleDeleteQuestion = (qIndex: number) => {
        const updatedQuestions = [...questions];
        updatedQuestions.splice(qIndex, 1);
        setQuestions(updatedQuestions);
    };
    const handleAddOption = (qIndex: number) => {
        const updatedQuestions = [...questions];
        updatedQuestions[qIndex].options.push({
            content: '',
            isCorrect: false,
            image: null,
        });
        setQuestions(updatedQuestions);
    };

    return (
        <div className="p-4 flex justify-center w-full">
            <div className="w-full">
                {loading ? (
                    <div className="flex justify-center items-center h-40">
                        <CircularProgress />
                    </div>
                ) : (
                    paginatedQuestions.map((q: any, qIndex: number) => {
                        const realIndex = page * PAGE_SIZE + qIndex;

                        return (
                            <Card
                                ref={(el) => (questionRefs.current[realIndex] = el)}
                                key={realIndex}
                                className="mb-6"
                                sx={{
                                    border: hasQuestionError(realIndex)
                                        ? '2px solid #d32f2f'
                                        : '1px solid #cac6e0',
                                }}
                            >
                                <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-b">
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm font-semibold">Question</span>
                                        <TextField
                                            size="small"
                                            type="number"
                                            inputProps={{ min: 1 }}
                                            value={q.displayOrder ?? realIndex + 1}
                                            sx={{ width: 70 }}
                                            onChange={(e) =>
                                                handleQuestionChange(
                                                    realIndex,
                                                    'displayOrder',
                                                    Number(e.target.value)
                                                )
                                            }
                                        />
                                        {hasQuestionError(realIndex) && (
                                            <span className="text-xs text-red-600 font-semibold">
                                                Error
                                            </span>
                                        )}
                                    </div>

                                    <Button
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteIcon fontSize="small" />}
                                        sx={{
                                            textTransform: 'none',
                                            minWidth: 'auto',
                                            '& .MuiButton-startIcon': {
                                                marginRight: '4px',
                                            },
                                        }}
                                        onClick={() => handleDeleteQuestion(realIndex)}
                                    >
                                        Remove
                                    </Button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4 py-3">
                                    <div>
                                        <span className="text-sm font-semibold">Question Type</span>

                                        <TextField
                                            select
                                            size="small"
                                            fullWidth
                                            value={q.questionType || 'MULTIPLE'}
                                            onChange={(e) =>
                                                handleQuestionChange(
                                                    realIndex,
                                                    'questionType',
                                                    e.target.value
                                                )
                                            }
                                            SelectProps={{
                                                MenuProps: {
                                                    disablePortal: true,
                                                },
                                            }}
                                        >
                                            <MenuItem value="MULTIPLE">Multiple Choice</MenuItem>
                                            <MenuItem value="SINGLE">Single Choice</MenuItem>
                                            <MenuItem value="NUMERIC">Numeric Choice</MenuItem>
                                        </TextField>
                                    </div>

                                    <div className="w-full">
                                        <span className="text-sm font-semibold block mb-1">
                                            Question Text
                                        </span>

                                        <TextField
                                            multiline
                                            minRows={4}
                                            fullWidth
                                            size="small"
                                            value={q.content || ''}
                                            error={!!errors?.[realIndex]?.question}
                                            helperText={errors?.[realIndex]?.question || ''}
                                            onChange={(e) =>
                                                handleQuestionChange(realIndex, 'content', e.target.value)
                                            }
                                        />

                                        {q.image && (
                                            <div className="mt-2">
                                                <img
                                                    src={q.image}
                                                    alt="Question image"
                                                    className="max-h-48 rounded border border-slate-300 object-contain"
                                                />
                                            </div>
                                        )}
                                    </div>                                </div>
                                <div className="flex justify-between p-2 items-center mb-2">
                                    <span className="font-semibold text-sm">Answer Options</span>

                                    <Button
                                        variant="text"
                                        size="small"
                                        sx={{
                                            color: '#1e88e5',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                        }}
                                        startIcon={
                                            <AddCircle sx={{ fontSize: 22 }} />
                                        }
                                        onClick={() => handleAddOption(realIndex)}
                                    >
                                        Add Option
                                    </Button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
                                    {q.options.map((opt: any, oIndex: number) => (
                                        <div key={oIndex} className="flex items-start gap-2">
                                            <Checkbox checked={opt.isCorrect} onChange={() => handleSetCorrectOption(realIndex, oIndex)} />

                                            <div className="flex-1">
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    value={opt.content}
                                                    error={!!errors?.[realIndex]?.options?.[oIndex]}
                                                    helperText={errors?.[realIndex]?.options?.[oIndex] || ''}
                                                    onChange={(e) =>
                                                        handleOptionChange(realIndex, oIndex, e.target.value)
                                                    }
                                                    InputProps={{
                                                        endAdornment: (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteOption(realIndex, oIndex)}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        ),
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {errors?.[realIndex]?.correctOption && (
                                    <div className="px-4 mt-1">
                                        <Typography variant="caption" color="error">
                                            {errors[realIndex].correctOption}
                                        </Typography>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-8 px-4 py-4">
                                    <div className="flex flex-col gap-4">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm font-semibold">Positive Marks</span>
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={q.positiveMarks}
                                                sx={{ width: 300 }}
                                                onChange={(e) => handleQuestionChange(realIndex, 'positiveMarks', e.target.value)}
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm font-semibold">Negative Marks</span>
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={q.negativeMarks}
                                                onChange={(e) => handleQuestionChange(realIndex, 'negativeMarks', e.target.value)}
                                                sx={{ width: 300 }}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <span className="text-sm font-semibold">Solution Description</span>
                                        <TextField
                                            multiline
                                            minRows={6}
                                            fullWidth
                                            value={q.solutionDescription}
                                            onChange={(e) => handleQuestionChange(realIndex, 'solutionDescription', e.target.value)}
                                            size="small"
                                        />
                                    </div>
                                </div>

                            </Card>
                        );
                    })
                )}
            </div>
        </div>
    );
};

const DocQuestionsPrev = ({
    questionsList,
    handleClose,
    open,
    path,
    sectionId,
}: {
    questionsList: any;
    handleClose: any;
    open: any;
    path: any;
    sectionId: any;
}) => {
    const [questions, setQuestions] = useState([...questionsList]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<any>({});
    const [page, setPage] = useState(0);
    const [errorPages, setErrorPages] = useState<number[]>([]);
    const totalPages = Math.ceil(questions.length / PAGE_SIZE);
    const record = useRecordContext();
    const refresh = useRefresh();
    const notify = useNotify();
    const [scrollToQuestion, setScrollToQuestion] = useState<number | null>(null);

    // ------------ path is test ---------------------
    const validateQuestions = () => {
        const newErrors: any = {};
        const pagesWithErrors = new Set<number>();
        let valid = true;
        let firstErrorIndex: number | null = null;

        questions.forEach((q, qIndex) => {
            const pageIndex = Math.floor(qIndex / PAGE_SIZE);

            if (!q.content?.trim()) {
                valid = false;
                pagesWithErrors.add(pageIndex);
                newErrors[qIndex] = { question: 'Question cannot be empty' };
                if (firstErrorIndex === null) firstErrorIndex = qIndex;
            }

            q.options.forEach((opt: any, oIndex: number) => {
                if (!opt.content?.trim()) {
                    valid = false;
                    pagesWithErrors.add(pageIndex);
                    if (!newErrors[qIndex]) newErrors[qIndex] = {};
                    if (!newErrors[qIndex].options) newErrors[qIndex].options = {};
                    newErrors[qIndex].options[oIndex] = 'Option cannot be empty';
                    if (firstErrorIndex === null) firstErrorIndex = qIndex;
                }
            });

            if (!q.options.some((opt: any) => opt.isCorrect)) {
                valid = false;
                pagesWithErrors.add(pageIndex);
                if (!newErrors[qIndex]) newErrors[qIndex] = {};
                newErrors[qIndex].correctOption =
                    'Please select at least one correct answer';
                if (firstErrorIndex === null) firstErrorIndex = qIndex;
            }
        });

        setErrors(newErrors);
        setErrorPages(Array.from(pagesWithErrors));

        return {
            valid,
            firstErrorPage: pagesWithErrors.values().next().value,
            firstErrorIndex,
        };
    };

    const handleSubmit = async () => {
        const { valid, firstErrorIndex } = validateQuestions();

        if (!valid) {
            if (firstErrorIndex !== null) {
                setScrollToQuestion(firstErrorIndex);

                const targetPage = Math.floor(firstErrorIndex / PAGE_SIZE);
                if (targetPage !== page) {
                    setPage(targetPage);
                }
            }
            return;
        }
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('No authentication token found');
            setLoading(false);
            return;
        }

        try {
            const result = await goApi.post('/questions/bulk', { questions });
            let testId = record?.test_id || record?.testId || record?.id;

            if (path === '/test') {
                const data = {
                    sectionId: sectionId || record?.id,
                    questions: result?.data?.map((q: any, index: number) => ({
                        id: q.id,
                        displayOrder: q.displayOrder ?? index + 1,
                        positiveMarks: q.positiveMarks ?? 0,
                        negativeMarks: q.negativeMarks ?? 0,
                        solutionDescription: q.solutionDescription ?? '',
                    })),
                };

                await goApi.post(`/sections-question-mappings/${testId}/bulk`, data);
                notify('Question Added successfully', {
                    type: 'success',
                });
                refresh();
            }
        } catch (error) {
            console.error('Error submitting questions:', error);
        } finally {
            setLoading(false);
            handleClose();
            refresh();
        }
    };
    function handlePageChange(newPage: number): void {
        const totalPages = Math.max(1, Math.ceil(questions.length / PAGE_SIZE));
        if (newPage < 0 || newPage >= totalPages) {
            return;
        }
        setPage(newPage);
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            sx={{ '& .MuiDialog-paper': { width: '90vw', maxWidth: '90vw', height: '95vh' } }}
        >
            <div className="flex flex-row justify-between">
                <DialogTitle>Edit Questions</DialogTitle>
            </div>
            {loading ? (
                <LoadingSection />
            ) : (
                <>
                    <DialogContent className="">
                        <QuestionsView
                            questions={questions}
                            setQuestions={setQuestions}
                            errors={errors}
                            setErrors={setErrors}
                            page={page}
                            setPage={setPage}
                            scrollToQuestion={scrollToQuestion}
                            setScrollToQuestion={setScrollToQuestion}

                        />
                    </DialogContent>
                    <DialogActions
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 8,
                        }}
                    >
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 0}
                                aria-label="Previous page"

                            >
                                <ChevronLeftIcon fontSize="small" />
                            </Button>

                            {Array.from({ length: totalPages }).map((_, index) => {
                                const hasError = errorPages.includes(index);
                                const isActive = index === page;

                                return (
                                    <Button
                                        key={index}
                                        size="small"
                                        onClick={() => handlePageChange(index)}
                                        variant={isActive ? 'contained' : 'outlined'}
                                        sx={{
                                            minWidth: 32,

                                            ...(hasError && {
                                                borderColor: 'error.main',
                                                color: isActive ? '#fff' : 'error.main',
                                                backgroundColor: isActive ? 'error.main' : 'error.lighter',
                                                '&:hover': {
                                                    backgroundColor: isActive ? 'error.dark' : 'error.light',
                                                    borderColor: 'error.dark',
                                                },
                                            }),

                                            ...(!hasError &&
                                                isActive && {
                                                backgroundColor: 'primary.main',
                                                color: '#fff',
                                            }),
                                        }}
                                    >
                                        {index + 1}
                                    </Button>
                                );
                            })}

                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === totalPages - 1}
                                aria-label="Next page"
                            >
                                <ChevronRightIcon fontSize="small" />
                            </Button>                        </div>

                        <div className="flex gap-3">
                            <Button onClick={handleClose}>Cancel</Button>
                            <Button variant="contained" onClick={handleSubmit}>
                                Save all Changes
                            </Button>
                        </div>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default DocQuestionsPrev;
